<template>
    <div class="content lic-detail-page">
        <div class="container">
            <!-- 페이지 타이틀 -->
            <div class="page-title">
                <button class="btn-back" @click="goBack">← 돌아가기</button>
                <h2 class="tit">{{ state.productName }}</h2>
            </div>

            <!-- 라이선스 정보 요약 -->
            <div class="license-summary" v-if="state.licenseInfo">
                <div class="summary-item">
                    <span class="label">총 라이선스:</span>
                    <span class="value">{{ state.licenseInfo.totalQuantity }}개</span>
                </div>
                <div class="summary-item">
                    <span class="label">할당됨:</span>
                    <span class="value">{{ state.licenseInfo.assignedQuantity }}개</span>
                </div>
                <div class="summary-item">
                    <span class="label">남은 수량:</span>
                    <span class="value">{{ state.licenseInfo.remainingQuantity }}개</span>
                </div>
            </div>

            <!-- 할당된 팀원 목록 -->
            <div class="panel">
                <div class="panel-header">
                    <div class="left">
                        <strong>할당된 팀원</strong>
                        <p class="total">총 <span>{{ state.memberList.length }}</span>명이 할당받았습니다.</p>
                    </div>
                    <div class="right">
                        <div class="btn-area text-right">
                            <button class="btn-submit" @click="openAssignModal" :disabled="state.licenseInfo && state.licenseInfo.remainingQuantity <= 0">
                                사용자 추가
                            </button>
                        </div>
                    </div>
                </div>

                <div class="table-wrapper">
                    <table class="table">
                        <thead>
                            <tr>
                                <th>이름</th>
                                <th>이메일</th>
                                <th style="width: 100px"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="state.loading.members">
                                <td colspan="3" class="empty">할당된 팀원 목록 불러오는 중...</td>
                            </tr>
                            <tr v-else-if="state.memberList.length === 0">
                                <td colspan="3" class="empty">할당된 팀원이 없습니다.</td>
                            </tr>
                            <tr v-for="m in state.memberList" :key="m[MEMBER_KEYS.id]">
                                <td>{{ m[MEMBER_KEYS.name] }}</td>
                                <td>{{ m[MEMBER_KEYS.email] }}</td>
                                <td>
                                    <button 
                                        class="btn-delete-small" 
                                        @click="unassignMember(m[MEMBER_KEYS.id])"
                                        :disabled="m.groupAssignment || m.offlineAssignment"
                                        :title="getUnassignButtonTitle(m)"
                                    >
                                        할당 해제
                                    </button>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <!-- [모달] 라이선스 할당 -->
            <div v-if="state.modals.assign" class="ss-modal-backdrop" @click.self="closeAssignModal">
                <div class="ss-modal modal-wide ss-modal--scroll-body">
                    <h3>라이선스 할당</h3>
                    <div class="ss-modal-scroll-body">
                    
                    <!-- 라이선스 선택 드롭다운 -->
                    <div class="form">
                        <label>라이선스 선택</label>
                        <select v-model="state.selectedLicenseId" @change="onChangeLicenseSelection" class="license-select">
                            <option value="auto">자동 지정 (남은 수량: {{ remainingLicenseCount }}개)</option>
                            <option 
                                v-for="lic in allLicenses" 
                                :key="lic.licenseId" 
                                :value="lic.licenseId"
                                :disabled="lic.remainingQuantity < selectedCount">
                                S/N: {{ lic.serialNumber }} | 만료일: {{ formatDate(lic.endDate) }} | 남은 수량: {{ lic.remainingQuantity }}개
                            </option>
                        </select>
                    </div>

                    <div class="modal-filter">
                        <div class="filter-left">
                            <label class="filter-checkbox">
                                <input type="checkbox" v-model="state.showAssignedMembers" />
                                <span>할당받은 멤버 보이기</span>
                            </label>
                            <span class="filter-info">(할당됨: {{ assignedMemberCount }}명)</span>
                        </div>
                        <div class="filter-right">
                            <span class="remaining-info">선택됨: <strong>{{ selectedCount }}</strong>명 / 최대: <strong>{{ maxCheckableCount }}</strong>명</span>
                        </div>
                    </div>
                    
                    <!-- 검색 입력 필드 -->
                    <div class="modal-search">
                        <input type="text" 
                               v-model="state.searchKeyword" 
                               placeholder="이름 또는 이메일로 검색..." 
                               class="search-input" />
                    </div>
                    
                    <p class="muted">{{ state.productName }} 할당 가능 팀원 목록</p>
                    <div class="invite-table-wrapper">
                        <div v-if="state.loading.assignModal" class="empty">불러오는 중…</div>
                        <template v-else>
                            <div v-if="filteredAssignModalMembers.length === 0" class="empty">
                                {{ state.searchKeyword.trim() ? '검색 결과가 없습니다.' : '할당 가능한 팀원이 없습니다.' }}
                            </div>
                            <table v-else class="invite-table">
                                <thead>
                                    <tr>
                                        <th style="width: 50px">
                                            <input type="checkbox"
                                                   :checked="isAllSelected"
                                                   :indeterminate.prop="isIndeterminate"
                                                   @change="toggleAllMembers($event)" />
                                        </th>
                                        <th>이름</th>
                                        <th>이메일</th>
                                        <th style="width: 80px">상태</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="m in filteredAssignModalMembers" :key="m.id">
                                        <td>
                                            <input type="checkbox"
                                                   :checked="state.selectedMemberIds.includes(m[MEMBER_KEYS.id])"
                                                   :disabled="m.isAssigned || (!canCheckMore && !state.selectedMemberIds.includes(m[MEMBER_KEYS.id]))"
                                                   @change="onToggleMemberCheckbox(m, $event)" />
                                        </td>
                                        <td>{{ m[MEMBER_KEYS.name] }}</td>
                                        <td>{{ m[MEMBER_KEYS.email] }}</td>
                                        <td>
                                            <span v-if="m.isAssigned" class="added-label">할당됨</span>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </template>
                    </div>
                    </div>
                    <div class="actions">
                        <button class="btn-submit" @click="submitAssignLicenses" :disabled="selectedCount === 0">할당 ({{ selectedCount }}명)</button>
                        <button class="btn-submit" @click="closeAssignModal">닫기</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'licenseDetail',
    data() {
        return {
            MEMBER_KEYS: { id: 'id', name: 'userName', email: 'email', role: 'role', roleName: 'roleName', caidx: 'caidx' },
            
            state: {
                crewId: null,
                productName: null,
                loading: { members: false, assignModal: false, licenseInfo: false },
                memberList: [],
                modals: { assign: false },
                assignModalMembers: [],
                licenseInfo: null,
                showAssignedMembers: false, // 할당받은 멤버 보이기 체크박스 상태
                searchKeyword: '', // 사용자 검색 키워드
                selectedMemberIds: [], // 체크박스로 선택된 멤버 ID 목록
                selectedLicenseId: 'auto', // 선택된 라이선스 ID ('auto' 또는 특정 licenseId)
            },
        };
    },
    computed: {
        filteredAssignModalMembers() {
            if (!this.state.assignModalMembers) return [];
            
            let filtered = this.state.assignModalMembers;
            
            // 1. 할당 상태 필터링
            if (!this.state.showAssignedMembers) {
                filtered = filtered.filter(m => !m.isAssigned);
            }
            
            // 2. 검색 키워드 필터링
            if (this.state.searchKeyword.trim()) {
                const keyword = this.state.searchKeyword.toLowerCase().trim();
                filtered = filtered.filter(m => {
                    const name = (m[this.MEMBER_KEYS.name] || '').toLowerCase();
                    const email = (m[this.MEMBER_KEYS.email] || '').toLowerCase();
                    return name.includes(keyword) || email.includes(keyword);
                });
            }
            
            return filtered;
        },
        assignedMemberCount() {
            if (!this.state.assignModalMembers) return 0;
            return this.state.assignModalMembers.filter(m => m.isAssigned).length;
        },
        remainingLicenseCount() {
            if (!this.state.licenseInfo) return 0;
            return this.state.licenseInfo.remainingQuantity || 0;
        },
        // 현재 선택된 멤버 수
        selectedCount() {
            return this.state.selectedMemberIds.length;
        },
        // 체크 가능한 최대 수
        maxCheckableCount() {
            if (this.state.selectedLicenseId === 'auto') {
                // 자동 지정: 총 남은 수량
                return this.remainingLicenseCount;
            } else {
                // 특정 라이선스: 해당 라이선스의 남은 수량
                const selectedLicense = this.getSelectedLicenseDetail();
                return selectedLicense ? selectedLicense.remainingQuantity : 0;
            }
        },
        // 더 체크할 수 있는지 여부
        canCheckMore() {
            return this.selectedCount < this.maxCheckableCount;
        },
        // 모든 라이선스 목록 (수량 부족한 것은 비활성화 표시용)
        allLicenses() {
            if (!this.state.licenseInfo || !this.state.licenseInfo.licenses) return [];
            return this.state.licenseInfo.licenses;
        },
        // 전체 선택 체크박스 상태
        isAllSelected() {
            const unassignedMembers = this.filteredAssignModalMembers.filter(m => !m.isAssigned);
            if (unassignedMembers.length === 0) return false;
            
            const selectableCount = Math.min(unassignedMembers.length, this.maxCheckableCount);
            return this.selectedCount === selectableCount && selectableCount > 0;
        },
        // 전체 선택 체크박스가 일부만 선택된 상태인지
        isIndeterminate() {
            return this.selectedCount > 0 && !this.isAllSelected;
        },
    },
    methods: {
        // ===== 공통 유틸 =====
        getData(resOrData) { return resOrData?.data ?? resOrData; },
        getSafeArray(resOrData) {
            const result = this.getData(resOrData);
            if (result?.status && result.status !== 'ok') {
                this.gritterErr(result?.msg || '요청이 실패했습니다.');
                return [];
            }
            const topCandidates = [result?.list, result?.rows, result?.content, result?.data];
            for (const v of topCandidates) if (Array.isArray(v)) return v;
            const nestedCandidates = [result?.data?.list, result?.data?.rows, result?.data?.content];
            for (const v of nestedCandidates) if (Array.isArray(v)) return v;
            if (result && typeof result === 'object') {
                for (const k of Object.keys(result)) if (Array.isArray(result[k])) return result[k];
            }
            return [];
        },

        // ===== 네비게이션 =====
        goBack() {
            // 사용자 관리의 제품별 탭으로 돌아가기
            this.$router.push({ 
                name: 'licenseManagement',
                query: { 
                    crewId: this.state.crewId,
                    mainTab: 'user',
                    subTab: 'products'
                }
            });
        },

        // ===== 초기화 =====
        bootstrap() {
            this.state.crewId = this.$route.params.crewId;
            this.state.productName = decodeURIComponent(this.$route.params.productName || '');

            if (!this.state.crewId || !this.state.productName) {
                this.gritterErr('잘못된 접근입니다.');
                this.$router.push({ name: 'licenseManagement' });
                return;
            }

            this.fetchLicenseInfo();
            this.fetchAssignedMembers();
        },

        // ===== 라이선스 정보 조회 =====
        fetchLicenseInfo() {
            if (!this.state.crewId || !this.state.productName) return;
            this.state.loading.licenseInfo = true;
            this.axiosCaller.get(
                this,
                this.APIs.LICENSE + '/getCrewLicenseInfoOne',
                { 
                    crewId: this.state.crewId,
                    productName: this.state.productName
                },
                (result) => {
                    try {
                        const data = result?.data || {};
                        if (data.status === 'ok') {
                            // 직접 라이선스 정보를 받음 (배열이 아닌 단일 객체)
                            this.state.licenseInfo = data.licenseInfo || null;
                        } else {
                            this.gritterErr(data.msg || '라이선스 정보 조회 실패');
                        }
                    } finally {
                        this.state.loading.licenseInfo = false;
                    }
                }
            );
        },

        // ===== 할당된 팀원 목록 조회 =====
        fetchAssignedMembers() {
            if (!this.state.crewId || !this.state.productName) {
                this.state.memberList = [];
                return;
            }
            this.state.loading.members = true;
            
            this.axiosCaller.get(
                this,
                this.APIs.LICENSE + '/getLicenseAssignedMembers',
                { 
                    crewId: this.state.crewId,
                    productName: this.state.productName
                },
                (result) => {
                    try {
                        const data = result?.data || {};
                        if (data.status === 'ok') {
                            this.state.memberList = this.getSafeArray(result);
                        } else {
                            this.gritterErr(data.msg || '팀원 목록 조회 실패');
                            this.state.memberList = [];
                        }
                    } catch (error) {
                        console.error('fetchAssignedMembers error:', error);
                        this.gritterErr('팀원 목록 조회 실패');
                        this.state.memberList = [];
                    } finally {
                        this.state.loading.members = false;
                    }
                }
            );
        },

        // ===== 라이선스 할당 모달 =====
        openAssignModal() {
            if (!this.state.crewId || !this.state.productName) return;
            this.state.modals.assign = true;
            this.state.showAssignedMembers = false; // 기본값: 할당받지 않은 멤버만 표시
            this.state.searchKeyword = ''; // 검색 키워드 초기화
            this.state.selectedMemberIds = []; // 선택 초기화
            this.state.selectedLicenseId = 'auto'; // 라이선스 선택 초기화
            this.prepareAssignModalMembers();
        },

        closeAssignModal() {
            this.state.modals.assign = false;
            this.state.selectedMemberIds = [];
            this.state.selectedLicenseId = 'auto';
        },

        // 선택된 라이선스 상세 정보 가져오기
        getSelectedLicenseDetail() {
            if (!this.state.licenseInfo || !this.state.licenseInfo.licenses) return null;
            if (this.state.selectedLicenseId === 'auto') return null;
            return this.state.licenseInfo.licenses.find(lic => lic.licenseId == this.state.selectedLicenseId);
        },

        // 전체 선택/해제 토글
        toggleAllMembers(evt) {
            const checked = !!evt?.target?.checked;
            
            if (checked) {
                // 전체 선택: 할당되지 않은 멤버 중 최대 수량까지만 선택
                const unassignedMembers = this.filteredAssignModalMembers.filter(m => !m.isAssigned);
                const selectableCount = Math.min(unassignedMembers.length, this.maxCheckableCount);
                
                this.state.selectedMemberIds = unassignedMembers
                    .slice(0, selectableCount)
                    .map(m => m[this.MEMBER_KEYS.id]);
            } else {
                // 전체 해제
                this.state.selectedMemberIds = [];
            }
        },

        // 체크박스 토글 (할당하지 않고 선택만)
        onToggleMemberCheckbox(member, evt) {
            const checked = !!evt?.target?.checked;
            const memberId = member[this.MEMBER_KEYS.id];

            if (checked) {
                // 체크 제한 확인
                if (!this.canCheckMore) {
                    evt.target.checked = false;
                    this.gritterErr(`최대 ${this.maxCheckableCount}명까지만 선택할 수 있습니다.`);
                    return;
                }
                // 선택 추가
                if (!this.state.selectedMemberIds.includes(memberId)) {
                    this.state.selectedMemberIds.push(memberId);
                }
            } else {
                // 선택 제거
                const index = this.state.selectedMemberIds.indexOf(memberId);
                if (index > -1) {
                    this.state.selectedMemberIds.splice(index, 1);
                }
            }
        },

        // 라이선스 선택 변경 시
        onChangeLicenseSelection() {
            // 선택된 라이선스의 남은 수량보다 많이 체크되어 있으면 경고
            if (this.selectedCount > this.maxCheckableCount) {
                this.gritterErr(`선택한 라이선스는 ${this.maxCheckableCount}개만 할당 가능합니다. 현재 ${this.selectedCount}명이 선택되어 있습니다.`);
                // 이전 선택으로 되돌리기
                this.state.selectedLicenseId = 'auto';
            }
        },

        // 할당 버튼 클릭
        submitAssignLicenses() {
            if (this.state.selectedMemberIds.length === 0) {
                this.gritterErr('할당할 멤버를 선택해주세요.');
                return;
            }

            if (this.state.selectedMemberIds.length > this.maxCheckableCount) {
                this.gritterErr(`최대 ${this.maxCheckableCount}명까지만 할당할 수 있습니다.`);
                return;
            }

            const payload = {
                crewId: this.state.crewId,
                productName: this.state.productName,
                memberIds: this.state.selectedMemberIds.join(','),
            };

            // 특정 라이선스 선택 시 licenseId 추가
            if (this.state.selectedLicenseId !== 'auto') {
                payload.licenseId = this.state.selectedLicenseId;
            }

            this.axiosCaller.post(
                this,
                this.APIs.LICENSE + '/assignLicenseBatch',
                payload,
                (result) => {
                    const data = result?.data || {};
                    if (data.status === 'ok') {
                        this.gritterOk(`${this.state.selectedMemberIds.length}명에게 라이선스가 할당되었습니다.`);
                        this.closeAssignModal();
                        this.fetchAssignedMembers();
                        this.fetchLicenseInfo();
                    } else {
                        this.gritterErr(data.msg || '라이선스 할당 실패');
                    }
                }
            );
        },

        formatDate(dateString) {
            if (!dateString) return '-';
            const date = new Date(dateString);
            return date.toLocaleDateString('ko-KR');
        },

        async prepareAssignModalMembers() {
            this.state.loading.assignModal = true;
            try {
                // 1. 해당 제품의 라이선스를 할당받은 팀원 목록 조회
                const assignedRes = await new Promise((resolve) => {
                    this.axiosCaller.get(
                        this,
                        this.APIs.LICENSE + '/getLicenseAssignedMembers',
                        { 
                            crewId: this.state.crewId,
                            productName: this.state.productName
                        },
                        (r) => resolve(r)
                    );
                });
                
                const assignedMembers = this.getSafeArray(assignedRes);
                const assignedMemberIds = new Set(assignedMembers.map(m => m.id));
                
                // 2. 전체 팀원 목록 조회
                const allMembersRes = await new Promise((resolve) => {
                    this.axiosCaller.get(
                        this,
                        this.APIs.LICENSE + '/getCrewMemberList',
                        { crewId: this.state.crewId },
                        (r) => resolve(r)
                    );
                });
                
                const allMembers = this.getSafeArray(allMembersRes);
                
                // 3. 모든 팀원에 할당 상태 추가
                const membersWithStatus = allMembers
                    .map(member => ({
                        ...member,
                        isAssigned: assignedMemberIds.has(member.id)
                    }))
                    .sort((a, b) => {
                        const nameA = a[this.MEMBER_KEYS.name] || '';
                        const nameB = b[this.MEMBER_KEYS.name] || '';
                        return nameA.localeCompare(nameB);
                    });
                
                this.state.assignModalMembers = membersWithStatus;
            } catch (error) {
                console.error('prepareAssignModalMembers error:', error);
                this.gritterErr('팀원 목록 조회 실패');
            } finally {
                this.state.loading.assignModal = false;
            }
        },


        getUnassignButtonTitle(member) {
            if (member.groupAssignment && member.offlineAssignment) {
                return '그룹 할당 및 오프라인 할당은 개별 해제할 수 없습니다';
            } else if (member.groupAssignment) {
                return '그룹 할당은 개별 해제할 수 없습니다';
            } else if (member.offlineAssignment) {
                return '오프라인 할당은 개별 해제할 수 없습니다';
            }
            return '할당 해제';
        },

        unassignMember(memberId) {
            if (!this.state.crewId || !this.state.productName || !memberId) return;
            
            const member = this.state.memberList.find(m => m[this.MEMBER_KEYS.id] === memberId);
            if (member) {
                if (member.groupAssignment && member.offlineAssignment) {
                    this.gritterErr('그룹 할당 및 오프라인 할당으로 할당된 라이선스는 개별 해제가 불가능합니다.');
                    return;
                } else if (member.groupAssignment) {
                    this.gritterErr('그룹 할당은 개별 해제할 수 없습니다.');
                    return;
                } else if (member.offlineAssignment) {
                    this.gritterErr('오프라인 할당은 개별 해제할 수 없습니다.');
                    return;
                }
            }
            
            if (!confirm('라이선스 할당을 해제하시겠습니까?')) return;
            
            const payload = {
                crewId: this.state.crewId,
                memberId: String(memberId),
                productName: this.state.productName,
            };
            this.axiosCaller.post(
                this,
                this.APIs.LICENSE + '/unassignLicense',
                payload,
                (result) => {
                    const data = result?.data || {};
                    if (data.status === 'ok') {
                        this.gritterOk('라이선스 할당이 해제되었습니다.');
                        // 로컬 상태 즉시 업데이트
                        if (this.state.licenseInfo) {
                            this.state.licenseInfo.assignedQuantity--;
                            this.state.licenseInfo.remainingQuantity++;
                        }
                        this.fetchAssignedMembers();
                        this.fetchLicenseInfo();
                    } else {
                        this.gritterErr(data.msg || '라이선스 할당 해제 실패');
                    }
                }
            );
        },

        // Toast → Gritter wrappers
        gritterOk(msg) { this.swalUtils?.gritter ? this.swalUtils.gritter('Success', msg || '성공', 'success', 2000) : console.log('[OK]', msg); },
        gritterErr(msg) { const m = msg || '정상적인 접근이 아닙니다. 다시 시도해주세요.'; this.swalUtils?.gritter ? this.swalUtils.gritter('error', m, 'error', 2000) : console.error('[ERR]', m); },
    },
    mounted() {
        // CSS 파일 동적 로드
        if (!document.querySelector('link[href="/ssflex/assets/css/crew-license-manager.css"]')) {
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = '/ssflex/assets/css/crew-license-manager.css';
            document.head.appendChild(link);
        }
        this.bootstrap();
    },
};
</script>

<style scoped>
/* 외부 CSS 파일 사용: /ssflex/assets/css/crew-license-manager.css */
/* 추가 컴포넌트별 스타일이 필요한 경우 crew-license-manager.css에 작성 */
</style>